import { useEffect, useState, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { connectSocket } from '../../utils/socket';
import api from '../../utils/api';

export default function AdminQuizLobby() {
  const { id: quizId } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const socketRef = useRef(null);
  const flagsRef = useRef({});

  const [sessionId, setSessionId] = useState(state?.sessionId || null);
  const [pin, setPin] = useState(state?.pin || '');
  const [participants, setParticipants] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [copied, setCopied] = useState(false);
  const [copiedGuest, setCopiedGuest] = useState(false);
  const [isDuel, setIsDuel] = useState(false);
  const [duelMosqueName, setDuelMosqueName] = useState('');

  useEffect(() => {
    if (sessionId) return;
    api.get(`/admin/quizzes/${quizId}`).then(r => {
      const q = r.data.quiz;
      setPin(q.pin || '');
      setIsDuel(q.is_duel || false);
      setDuelMosqueName(q.duel_mosque_name || '');
      flagsRef.current = { soundProjector: q.sound_projector === true, leaderboardProjector: q.leaderboard_projector !== false };
      return api.get(`/admin/quizzes/${quizId}/session`);
    }).then(r => {
      if (r.data.sessionId) setSessionId(r.data.sessionId);
      else setLoadError('Keine aktive Session gefunden. Bitte Quiz neu starten.');
    }).catch(() => setLoadError('Session konnte nicht geladen werden.'));
  }, [quizId, sessionId]);

  useEffect(() => {
    if (!sessionId) return;
    const socket = connectSocket();
    socketRef.current = socket;

    socket.emit('admin:join_session', { sessionId, mosqueId: 'admin' });

    socket.on('lobby:update', ({ participants: p }) => setParticipants(p));
    socket.on('quiz:started', () => navigate(`/admin/quiz-live/${sessionId}`, { state: flagsRef.current }));
    socket.on('session:state', (s) => {
      if (s.isDuel) setIsDuel(true);
      if (s.duelMosqueName) setDuelMosqueName(s.duelMosqueName);
    });

    return () => { socket.off('lobby:update'); socket.off('quiz:started'); socket.off('session:state'); };
  }, [sessionId, navigate]);

  const startQuiz = () => socketRef.current?.emit('admin:start', { sessionId });

  const copyToClipboard = (text) => {
    if (navigator.clipboard) {
      return navigator.clipboard.writeText(text);
    }
    const el = document.createElement('textarea');
    el.value = text;
    el.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    return Promise.resolve();
  };

  const copyLink = () => {
    copyToClipboard(joinUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const copyGuestLink = () => {
    copyToClipboard(guestUrl).then(() => {
      setCopiedGuest(true);
      setTimeout(() => setCopiedGuest(false), 2000);
    });
  };

  const joinUrl = pin ? `${window.location.origin}/join/${pin}` : '';
  const guestUrl = pin ? `${window.location.origin}/guest/${pin}` : '';

  if (loadError) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div className="card" style={{ textAlign: 'center', maxWidth: 400 }}>
          <p style={{ color: 'var(--error)', marginBottom: 16 }}>{loadError}</p>
          <button className="btn btn-primary" onClick={() => navigate('/admin/quizzes')}>Zuruck zu Quizze</button>
        </div>
      </div>
    );
  }

  if (!sessionId) {
    return <div className="spinner-wrap"><div className="spinner" /></div>;
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', padding: 32 }}>
      <div style={{ maxWidth: 800, margin: '0 auto' }}>
        <h1 style={{ fontFamily: 'Playfair Display', fontSize: 28, color: 'var(--text)', marginBottom: 8 }}>
          Quiz-Lobby
        </h1>

        {isDuel && duelMosqueName && (
          <div style={{ background: 'var(--primary-lt)', border: '1px solid var(--primary)', borderRadius: 10, padding: '10px 16px', marginBottom: 16, fontSize: 14 }}>
            <strong>Duell-Modus:</strong> {duelMosqueName} wurde herausgefordert
          </div>
        )}

        <div className="grid-2" style={{ marginBottom: 16 }}>
          {/* PIN */}
          <div className="card" style={{ textAlign: 'center' }}>
            <p style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 8 }}>PIN CODE</p>
            <div style={{ fontFamily: 'DM Sans', fontSize: 48, fontWeight: 700, letterSpacing: 12, color: 'var(--primary)' }}>
              {pin}
            </div>
            <p style={{ fontSize: 12, color: 'var(--text3)', marginTop: 8 }}>ilmify.ch/join</p>
          </div>

          {/* QR */}
          <div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
            {joinUrl && <QRCodeSVG value={joinUrl} size={140} fgColor="var(--text)" bgColor="transparent" />}
          </div>
        </div>

        {/* Teilnehmer-Link */}
        {joinUrl && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
            <div style={{ flex: 1, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {joinUrl}
            </div>
            <button className="btn btn-secondary btn-sm" onClick={copyLink} style={{ whiteSpace: 'nowrap' }}>
              {copied ? 'Kopiert!' : 'Link kopieren'}
            </button>
          </div>
        )}

        {/* Projektions-Link */}
        {guestUrl && (
          <div style={{
            background: isDuel ? 'var(--primary-lt)' : 'var(--surface)',
            border: `1px solid ${isDuel ? 'var(--primary)' : 'var(--border)'}`,
            borderRadius: 10, padding: '14px 16px', marginBottom: 24,
          }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
              {isDuel ? `Projektions-Link fur ${duelMosqueName || 'Gast-Moschee'}` : 'Projektions-Link (Zuschauer)'}
            </div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 10 }}>
              Diesen Link auf dem Beamer offnen — zeigt PIN + QR, dann live Zuschauer
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ flex: 1, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 13, color: 'var(--text3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {guestUrl}
              </div>
              <button className="btn btn-secondary btn-sm" onClick={copyGuestLink} style={{ whiteSpace: 'nowrap' }}>
                {copiedGuest ? 'Kopiert!' : 'Link kopieren'}
              </button>
              <button className="btn btn-primary btn-sm" onClick={() => window.open(guestUrl, '_blank', 'noopener')} style={{ whiteSpace: 'nowrap' }}>
                Vollbild öffnen
              </button>
            </div>
          </div>
        )}

        {/* Participants */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header">
            <h3 style={{ fontSize: 16 }}>Teilnehmer ({participants.length})</h3>
          </div>
          {participants.length === 0 ? (
            <p style={{ color: 'var(--text3)', textAlign: 'center', padding: 32 }}>
              Warte auf Teilnehmer...
            </p>
          ) : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {participants.map((p, i) => (
                <div key={i} style={{
                  background: 'var(--primary-lt)', color: 'var(--primary)',
                  padding: '6px 14px', borderRadius: 100, fontSize: 14, fontWeight: 500,
                }}>
                  {p.firstName} {p.lastName}
                  {p.ageGroupLabel && <span style={{ fontSize: 11, opacity: 0.7 }}> ({p.ageGroupLabel})</span>}
                  {p.team && <span style={{ fontSize: 11, opacity: 0.7 }}> · {p.team}</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <button
            className="btn btn-primary btn-lg"
            onClick={startQuiz}
            disabled={participants.length === 0}
          >
            Quiz starten ({participants.length} Teilnehmer)
          </button>
          <button className="btn btn-ghost btn-lg" onClick={() => navigate('/admin/quizzes')}>
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
}
